import React, { useEffect, useRef, useState } from 'react';
import { graphqlMutation, graphqlQuery } from '../utils/graphqlApi';
import { invokeAgent, submitAgentFeedback } from '../graphql/mutations';
import { getSessionTimeline } from '../graphql/queries';
import { AWAITING_APPROVAL_STATUSES } from '../utils/sessionHelpers';
import useUserPermissions from '../hooks/useUserPermissions';

function SnAnalysis() {
  const { canInvokeServiceNeedAgent } = useUserPermissions();

  // Strip URLs/hyperlinks from agent output (plain text only)
  const stripUrls = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text
      .replace(/\[([^\]]*)\]\([^)]+\)/g, '')     // [label](url) → remove entirely
      .replace(/https?:\/\/[^\s)>\]]+/g, '')     // bare URLs → remove entirely
      .replace(/\s{2,}/g, ' ')                   // collapse extra spaces
      .trim();
  };

  const [ask, setAsk] = useState('');
  const [status, setStatus] = useState('idle'); // idle | invoking | polling | done | error
  const [message, setMessage] = useState('');
  const [output, setOutput] = useState('');
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [feedbackValue, setFeedbackValue] = useState(null); // 'HELPFUL' | 'NOT_HELPFUL' | null
  const [writtenFeedback, setWrittenFeedback] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [findings, setFindings] = useState([]); // accumulated timeline entries for display on error/timeout
  const [serviceNeedCodeCheckReason, setServiceNeedCodeCheckReason] = useState('');
  const pollRef = useRef(null);

  const parseEntryData = (data) => {
    if (data == null) return null;
    let parsed = data;
    // Some timeline fields are double-encoded JSON strings.
    for (let i = 0; i < 3; i += 1) {
      if (typeof parsed !== 'string') return parsed;
      const trimmed = parsed.trim();
      if (!trimmed) return '';
      const looksLikeJson =
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('"') && trimmed.endsWith('"'));
      if (!looksLikeJson) return parsed;
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return parsed;
      }
    }
    return parsed;
  };

  const coerceOutputText = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') {
      const reparsed = parseEntryData(value);
      if (reparsed !== value) {
        return coerceOutputText(reparsed);
      }
      return value.trim();
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      const joined = value
        .map((item) => coerceOutputText(item))
        .filter(Boolean)
        .join('\n\n');
      return joined.trim();
    }
    if (typeof value === 'object') {
      const preferredKeys = [
        'output',
        'analysis',
        'result',
        'message',
        'detail',
        'reason',
        'summary',
        'text',
      ];
      for (const key of preferredKeys) {
        const extracted = coerceOutputText(value[key]);
        if (extracted) return extracted;
      }
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '';
      }
    }
    return '';
  };

  const deriveVisibleOutput = ({ latestStatusPayload, stepResultItems, agentMsgItems, decisionItems, approvalRequestItems }) => {
    // Priority: STEP_RESULT output first (this is the rich analysis content
    // shown on the PlanApproval page in the Incident Resolver flow).
    const sortedStepResults = stepResultItems
      .slice()
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    const codeReviewResult = sortedStepResults.find((entry) => {
      const payload = parseEntryData(entry.data);
      return payload?.step_id === 'code-review';
    });
    const preferredStepResult = codeReviewResult || sortedStepResults[0];
    if (preferredStepResult) {
      const payload = parseEntryData(preferredStepResult.data);
      // Extract the 'output' field specifically (matches PlanApproval behavior)
      const outputField = payload?.output || payload?.message || payload?.detail;
      if (outputField && typeof outputField === 'string' && outputField.trim()) {
        return outputField.trim();
      }
      const stepText = coerceOutputText(payload);
      if (stepText) return stepText;
    }

    // Next: AGENT_MSG entries (Genie reasoning with SQL)
    const latestAgentMsg = agentMsgItems
      .slice()
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
    if (latestAgentMsg) {
      const payload = parseEntryData(latestAgentMsg.data);
      const agentMessage = coerceOutputText(payload?.message || payload?.detail || payload);
      const queryText = coerceOutputText(payload?.query);
      if (agentMessage || queryText) {
        return [agentMessage, queryText ? `SQL Query:\n${queryText}` : ''].filter(Boolean).join('\n\n');
      }
    }

    // Then: STATUS detail (short progress messages)
    const statusText = coerceOutputText(latestStatusPayload);
    if (statusText) return statusText;

    // Then: DECISION entries
    const latestDecision = decisionItems
      .slice()
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
    if (latestDecision) {
      const payload = parseEntryData(latestDecision.data);
      const decisionText = coerceOutputText(payload);
      if (decisionText) return decisionText;
    }

    // Finally: APPROVAL_REQUEST entries
    const latestApproval = approvalRequestItems
      .slice()
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
    if (latestApproval) {
      const payload = parseEntryData(latestApproval.data);
      const approvalText = coerceOutputText(payload);
      if (approvalText) return approvalText;
    }

    return '';
  };

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const handleTrigger = async () => {
    if (!ask.trim()) {
      setStatus('error');
      setMessage('Please enter your request in Ask the Agent.');
      return;
    }

    if (!canInvokeServiceNeedAgent()) {
      setStatus('error');
      setMessage('Your role does not have permission to run SN Analysis.');
      return;
    }

    setStatus('invoking');
    setMessage('Starting SN agent...');
    setOutput('');
    setFeedbackValue(null);
    setWrittenFeedback('');
    setFeedbackSubmitted(false);
    setFeedbackError('');
    setFindings([]);
    setServiceNeedCodeCheckReason('');

    const nextSessionId = `ui-sn-analysis-${crypto.randomUUID()}`;
    setCurrentSessionId(nextSessionId);
    console.log('[SN Analysis] Starting invokeAgent with session:', nextSessionId);

    try {
      graphqlMutation(invokeAgent, {
        input: {
          short_description: ask,
          description: ask,
          tribe: 'PRM',
          payload: null,
          sessionId: nextSessionId,
          // Tells the backend this run originated from the SN Analysis page so
          // it keys the incident-table entry as ANA-<sessionId> (no real
          // incident number exists on this page).
          source: 'service_need_analysis',
        },
      })
        .then((res) => {
          const response = res?.invokeAgent;
          console.log('[SN Analysis] invokeAgent response:', JSON.stringify(response));
          if (response?.status === 'FAILED') {
            console.error('[SN Analysis] invokeAgent returned FAILED:', response?.error);
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
            setStatus('error');
            setMessage(response.error || 'SN agent invocation failed.');
          }
        })
        .catch((invokeErr) => {
          // Timeout can be expected for long-running async agents.
          console.warn('[SN Analysis] invokeAgent timeout/error (polling continues):', invokeErr.message);
        });

      setStatus('polling');
      setMessage('SN agent is running...');

      const start = Date.now();
      let pollCount = 0;
      let pollErrorCount = 0;
      pollRef.current = setInterval(async () => {
        pollCount += 1;
        const elapsedSec = ((Date.now() - start) / 1000).toFixed(1);
        console.log(`[SN Analysis] Poll #${pollCount} (${elapsedSec}s) session=${nextSessionId}`);
        try {
          const timelineRes = await graphqlQuery(getSessionTimeline, { sessionId: nextSessionId });
          pollErrorCount = 0;
          const allItems = timelineRes?.getSessionTimeline?.items || [];

          const statusItems = allItems.filter((e) => e?.entryType === 'STATUS');
          const decisionItems = allItems.filter((e) => e?.entryType === 'DECISION');
          const stepResultItems = allItems.filter((e) => e?.entryType === 'STEP_RESULT');
          const agentMsgItems = allItems.filter((e) => e?.entryType === 'AGENT_MSG');
          const approvalRequestItems = allItems.filter((e) => e?.entryType === 'APPROVAL_REQUEST');

          console.log(
            `[SN Analysis] Poll #${pollCount}: ${statusItems.length} STATUS, ${decisionItems.length} DECISION, ${stepResultItems.length} STEP_RESULT, ${approvalRequestItems.length} APPROVAL_REQUEST`
          );

          const SN_STATUS_LABELS = {
            QUERYING_GENIE: 'Querying Databricks Genie',
            EXECUTING_QUERY: 'Executing SQL query',
            GENIE_COMPLETED: 'Genie analysis complete',
            DECIDING: 'Decision step reached',
            REVIEWING_CODE: 'Code review in progress',
            COMPLETED: 'Completed',
            SKIPPED_CODE_REVIEW: 'Code review skipped',
            FAILED: 'Failed',
          };

          const entries = [];
          const sortedStatuses = statusItems.slice().sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
          for (const s of sortedStatuses) {
            const p = (() => {
              try {
                return typeof s.data === 'string' ? JSON.parse(s.data) : s.data;
              } catch {
                return null;
              }
            })();
            const st = (p?.status || s.status || '').toUpperCase();
            entries.push({
              label: SN_STATUS_LABELS[st] || `Status: ${st || 'UNKNOWN'}`,
              detail: p?.detail || p?.message || '',
              createdAt: s.createdAt,
            });
          }

          for (const d of decisionItems) {
            const p = (() => {
              try {
                return typeof d.data === 'string' ? JSON.parse(d.data) : d.data;
              } catch {
                return null;
              }
            })();
            entries.push({
              label: p?.invoke_code_checker ? 'Decision: Invoke code review' : 'Decision: Skip code review',
              detail: `${p?.service_need ? `SN: ${p.service_need}` : ''}${p?.reason ? ` - ${p.reason}` : ''}`,
              createdAt: d.createdAt,
            });
          }

          for (const sr of stepResultItems) {
            const p = (() => {
              try {
                return typeof sr.data === 'string' ? JSON.parse(sr.data) : sr.data;
              } catch {
                return null;
              }
            })();
            entries.push({
              label: `${p?.step_id || 'step'}: ${p?.status || sr.status || 'unknown'}`,
              detail: p?.output ? String(p.output).substring(0, 300) : '',
              createdAt: sr.createdAt,
            });
          }

          for (const am of agentMsgItems) {
            const p = (() => {
              try {
                return typeof am.data === 'string' ? JSON.parse(am.data) : am.data;
              } catch {
                return null;
              }
            })();
            entries.push({
              label: 'Genie Message',
              detail: `${(p?.message || '').substring(0, 200)}${p?.query ? `\nSQL: ${String(p.query).substring(0, 200)}` : ''}`,
              createdAt: am.createdAt,
            });
          }

          entries.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
          // Accumulate findings for display on error/timeout (parity with resolver flow).
          if (entries.length > 0) {
            setFindings(entries);
          }

          const latestStatus = statusItems
            .slice()
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];

          const latestStatusPayload = parseEntryData(latestStatus?.data);

          const visibleOutput = deriveVisibleOutput({
            latestStatusPayload,
            stepResultItems,
            agentMsgItems,
            decisionItems,
            approvalRequestItems,
          });

          const state = (latestStatusPayload?.status || latestStatus?.status || '').toUpperCase();
          if (state) {
            console.log(`[SN Analysis] Poll #${pollCount}: latest STATUS=${state} detail=${latestStatusPayload?.detail || latestStatusPayload?.message || '(none)'}`);
          }

          const SN_PROGRESS_LABELS = {
            QUERYING_GENIE: 'Querying Databricks Genie...',
            EXECUTING_QUERY: 'Executing SQL query...',
            GENIE_COMPLETED: 'Genie analysis complete. Making decision...',
            DECIDING: 'Deciding whether code review is needed...',
            REVIEWING_CODE: 'Reviewing code in GitLab... (this may take several minutes)',
          };
          if (SN_PROGRESS_LABELS[state]) {
            setMessage(SN_PROGRESS_LABELS[state]);
          } else if (latestStatusPayload?.message) {
            setMessage(latestStatusPayload.message);
          } else if (latestStatusPayload?.detail) {
            setMessage(String(latestStatusPayload.detail).substring(0, 200));
          }

          const latestStatusForApproval = statusItems
            .slice()
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
          const statusStateForApproval = (() => {
            try {
              const p = typeof latestStatusForApproval?.data === 'string'
                ? JSON.parse(latestStatusForApproval.data)
                : latestStatusForApproval?.data;
              return (p?.status || latestStatusForApproval?.status || '').toUpperCase();
            } catch {
              return '';
            }
          })();

          const awaitingApproval =
            approvalRequestItems.length > 0 ||
            AWAITING_APPROVAL_STATUSES.has(statusStateForApproval);

          if (awaitingApproval) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStatus('done');
            setMessage('Awaiting approval decision.');
            const latestApproval = approvalRequestItems
              .slice()
              .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
            const approvalText = (() => {
              try {
                const p = typeof latestApproval?.data === 'string' ? JSON.parse(latestApproval.data) : latestApproval?.data;
                return p?.message || p?.detail || '';
              } catch {
                return '';
              }
            })();
            setOutput(approvalText || visibleOutput || 'Service Need analysis is ready.');
            return;
          }

          if (state === 'FAILED' || state === 'REJECTED' || state === 'ACCESS_DENIED') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStatus('error');
            setMessage(
              latestStatusPayload?.detail ||
              latestStatusPayload?.error ||
              latestStatusPayload?.message ||
              (state === 'REJECTED'
                ? 'The agent rejected the request (approval may have timed out).'
                : state === 'ACCESS_DENIED'
                  ? 'Access denied — you do not have permission for this request.'
                  : 'SN agent failed.')
            );
            return;
          }

          if (state === 'COMPLETED' || state === 'SKIPPED_CODE_REVIEW') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStatus('done');
            setMessage('Analysis completed.');
            setOutput(visibleOutput || 'SN agent completed successfully.');
            return;
          }

          // Fallback: if code-review step result exists but terminal status wasn't written yet.
          const codeReviewResult = stepResultItems.find((e) => {
            try {
              const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
              return d?.step_id === 'code-review';
            } catch {
              return false;
            }
          });
          if (codeReviewResult) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStatus('done');
            setMessage('Analysis completed.');
            const codeReviewOutput = coerceOutputText(parseEntryData(codeReviewResult.data)) || 'Code review completed.';
            setOutput(codeReviewOutput);
            return;
          }

          // Fallback: skip-code-review decision written, terminal status missing.
          const skipDecision = decisionItems.find((e) => {
            try {
              const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
              return d?.invoke_code_checker === false;
            } catch {
              return false;
            }
          });
          if (skipDecision) {
            const decisionAge = Date.now() - new Date(skipDecision.createdAt || 0).getTime();
            if (decisionAge > 15000) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setStatus('done');
              setMessage('Analysis completed.');
              const skipReason = (() => {
                try {
                  const d = typeof skipDecision.data === 'string' ? JSON.parse(skipDecision.data) : skipDecision.data;
                  return d?.reason || 'Code review skipped.';
                } catch {
                  return 'Code review skipped.';
                }
              })();
              setOutput(skipReason);
              return;
            }
          }

          if (!latestStatus) {
            console.log(`[SN Analysis] Poll #${pollCount}: no entries yet`);
          }

          if (Date.now() - start > 900000) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStatus('error');
            const lastState = sortedStatuses.length > 0
              ? (() => {
                  try {
                    const p = typeof sortedStatuses[sortedStatuses.length - 1].data === 'string'
                      ? JSON.parse(sortedStatuses[sortedStatuses.length - 1].data)
                      : sortedStatuses[sortedStatuses.length - 1].data;
                    return p?.status || sortedStatuses[sortedStatuses.length - 1].status || '';
                  } catch {
                    return '';
                  }
                })()
              : '';

            // Extract INVOKE_CODE_CHECKER reason from DECISION entries
            const invokeCodeCheckerDecision = decisionItems.find((e) => {
              try {
                const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
                return d?.invoke_code_checker === true;
              } catch {
                return false;
              }
            });
            const invokeCodeCheckerReason = (() => {
              if (!invokeCodeCheckerDecision) return '';
              try {
                const d = typeof invokeCodeCheckerDecision.data === 'string'
                  ? JSON.parse(invokeCodeCheckerDecision.data)
                  : invokeCodeCheckerDecision.data;
                return d?.reason || '';
              } catch {
                return '';
              }
            })();

            if (invokeCodeCheckerReason) {
              setServiceNeedCodeCheckReason(invokeCodeCheckerReason);
            }

            setMessage(
              lastState
                ? `Agent stuck at "${lastState}" and timed out. See process details below.`
                : 'Service Need agent timed out. No progress entries were found.'
            );
            if (invokeCodeCheckerReason) {
              setOutput(`Code review reason: ${invokeCodeCheckerReason}`);
            }
            return;
          }
        } catch (err) {
          pollErrorCount += 1;
          console.warn(`[SN Analysis] Poll #${pollCount} error (${pollErrorCount}/5):`, err.message || err);
          if (pollErrorCount >= 5) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStatus('error');
            setMessage(err.message || 'Failed to poll SN agent status.');
          }
        }
      }, 4000);
    } catch (err) {
      setStatus('error');
      setMessage(err?.message || 'Failed to trigger SN agent.');
    }
  };

  const handleSubmitFeedback = async () => {
    if (!currentSessionId || !feedbackValue || feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    setFeedbackError('');
    try {
      await graphqlMutation(submitAgentFeedback, {
        input: {
          session_id: currentSessionId,
          feedback: feedbackValue,
          written_feedback: writtenFeedback || null,
        },
      });
      setFeedbackSubmitted(true);
    } catch (err) {
      console.error('[SN Analysis] submitAgentFeedback failed:', err);
      setFeedbackError(err?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <h2 className="kone-section-title mb-4">Service Need Analysis</h2>
      <div className="card p-4">
        <label htmlFor="sn-analysis-ask" className="kone-label">
          Ask the Agent
        </label>
        <textarea
          id="sn-analysis-ask"
          className="kone-input mb-3"
          rows={6}
          value={ask}
          onChange={(e) => setAsk(e.target.value)}
          placeholder="Describe what you want to analyze for Service Need..."
        />

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="kone-btn kone-btn-primary"
            onClick={handleTrigger}
            disabled={!canInvokeServiceNeedAgent() || status === 'invoking' || status === 'polling'}
          >
            {!canInvokeServiceNeedAgent()
              ? 'No Access'
              : status === 'invoking' || status === 'polling'
                ? 'Investigating...'
                : 'Investigate'}
          </button>
        </div>

        {(status === 'invoking' || status === 'polling') && (
          <div className="card p-4 mt-3">
            <div className="d-flex align-items-center gap-2">
              <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></div>
              <span>{stripUrls(message) || 'Service Need agent is running...'}</span>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="mt-3">
            <div className="alert alert-danger" style={{ whiteSpace: 'pre-line' }}>{stripUrls(message)}</div>
            {findings.length > 0 && (
              <div className="card p-3 mt-2">
                <h6 className="mb-2">Agent Progress (before failure/timeout):</h6>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {findings.map((f, i) => (
                    <div key={i} className="d-flex align-items-start gap-2 mb-2" style={{ fontSize: '0.9rem' }}>
                      <span style={{ whiteSpace: 'nowrap' }}>{stripUrls(f.label)}</span>
                      {f.detail && (
                        <span className="text-muted" style={{ wordBreak: 'break-word' }}>
                          {stripUrls(f.detail)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {status === 'done' && output && (
          <div className="card p-3 mt-3 investigate-report">
            <h6 className="mb-2">Service Need Analysis Output</h6>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'inherit' }}>
              {stripUrls(output)}
            </pre>
          </div>
        )}

        {status === 'done' && !output && (
          <div className="alert alert-warning mt-3 mb-0">
            Analysis completed but no output was generated.
          </div>
        )}

        {status === 'done' && output && (
          <div className="card p-3 mt-3" style={{ backgroundColor: '#f8f9fa' }}>
            <h5 className="kone-section-title">Was this investigation helpful?</h5>
            {feedbackSubmitted ? (
              <div className="alert alert-success py-2 px-3 mb-0">Feedback submitted. Thank you!</div>
            ) : (
              <>
                <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                  <button
                    type="button"
                    className={`kone-btn ${feedbackValue === 'HELPFUL' ? 'kone-btn-primary' : 'kone-btn-outline'}`}
                    onClick={() => setFeedbackValue('HELPFUL')}
                  >
                    👍 Helpful
                  </button>
                  <button
                    type="button"
                    className={`kone-btn ${feedbackValue === 'NOT_HELPFUL' ? 'kone-btn-danger' : 'kone-btn-secondary'}`}
                    onClick={() => setFeedbackValue('NOT_HELPFUL')}
                  >
                    👎 Not Helpful
                  </button>
                </div>
                <textarea
                  className="kone-input mb-2"
                  rows={2}
                  style={{ maxWidth: '500px', resize: 'vertical' }}
                  placeholder="Optional comment..."
                  value={writtenFeedback}
                  onChange={(e) => setWrittenFeedback(e.target.value)}
                />
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="kone-btn kone-btn-primary"
                    onClick={handleSubmitFeedback}
                    disabled={!feedbackValue || feedbackSubmitting}
                  >
                    {feedbackSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                  {feedbackError && (
                    <span className="text-danger"><small>{feedbackError}</small></span>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {serviceNeedCodeCheckReason && (
          <div className="card p-3 mt-2 border-warning">
            <h6 className="mb-2">Code Review Reason (INVOKE_CODE_CHECKER)</h6>
            <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{stripUrls(serviceNeedCodeCheckReason)}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SnAnalysis;
